import { compare } from "@/lib/controls/should-show-control";
import lodash from "lodash";
import { pipe } from "fp-ts/function";
import * as t from "io-ts";
import { fold } from "fp-ts/Either";
import { PopulateOptions } from "@/types/controls";
import defensiveCopy from "@/util/defensive-copy";

export const filterItem = (populate: PopulateOptions, item: unknown) => {
  const conditions = populate.condition;
  const decodedItem = decodeItem(item);

  // If the item doesn't match the type, exclude it from the populate array.
  if (!decodedItem) {
    return false;
  }

  if (populate?.text && populate?.value) {
    const itemLabel = lodash.get(decodedItem, populate?.text);
    const itemValue = lodash.get(decodedItem, populate?.value);

    // Don't show the item if it's fully empty.
    if (!itemLabel && !itemValue) {
      return false;
    }

    // It's ok to have a value without a label, but not the other way around.
    if (itemLabel && !itemValue) {
      return false;
    }
  }

  // If no condition is defined, we don't wanna do any comparison.
  if (!conditions) {
    return true;
  }

  // TODO %%CURRENTPATH%% is not supported here. Edge case, or on purpose?
  // Can be added by using "shouldShowControl" but we don't have the control path anywhere at hand

  if (Array.isArray(conditions)) {
    return (
      conditions
        .map(conditions => {
          return (
            conditions
              .map(condition => {
                const itemValue = lodash.get(decodedItem, condition.path);
                return compare(itemValue, condition.operand, condition.value);
              })
              // All conditions must be true to pass (AND)
              .filter(Boolean).length === conditions.length
          );
        })
        // Any condition passes? (OR)
        .filter(Boolean).length === conditions.length
    );
  } else {
    const itemValue = lodash.get(decodedItem, conditions.path);
    return compare(itemValue, conditions.operand, conditions.value);
  }
};

export const mapItem = (populate: PopulateOptions, item: unknown) => {
  const decodedItem = decodeItem(item);

  // If the item doesn't match the type, don't return anything.
  if (!decodedItem) {
    return null;
  }

  if (populate.text && populate.value) {
    const itemLabel = lodash.get(decodedItem, populate.text);
    const itemValue = lodash.get(decodedItem, populate.value);

    return {
      text: itemLabel ? itemLabel : itemValue,
      value: itemValue
    };
  }

  // The `value` key could be undefined, so we can just return an array of strings and the `v-select` component will know what to do.
  if (populate.text && lodash.get(decodedItem, populate.text)) {
    return lodash.get(decodedItem, populate.text);
  }

  return decodedItem;
};

export const decodeItem = (item: unknown) => {
  return pipe(
    item,
    t.record(t.string, t.unknown).decode,
    fold(
      () => null,
      unknownItem => unknownItem
    )
  );
};

export const fillEmptyLabelsWithValue = (
  populate: PopulateOptions,
  item: unknown
) => {
  const decodedItem = defensiveCopy(decodeItem(item));

  // If the item doesn't match the type, don't return anything.
  if (!decodedItem) {
    return null;
  }

  if (
    populate.text &&
    populate.value &&
    !lodash.get(decodedItem, populate.text) &&
    lodash.get(decodedItem, populate.value)
  ) {
    decodedItem[populate.text] = lodash.get(decodedItem, populate.value);
  }

  return decodedItem;
};
